import { defineResource } from 'pulse/resources';
import {
  defaultCrmSearchResolver,
  defaultCrmSearchAllPropertiesResolver,
} from './resolver';
import { CrmObject } from '../CrmObject';

export const CrmSearch = defineResource({
  typeName: 'hs-resources:CrmSearch',
  resolver: defaultCrmSearchResolver,
  args: [
    'filterGroups',
    'objectTypeId',
    'properties',
    'sorts',
    'query',
    'count',
    'offset',
    'timeZoneId',
    'timeZoneSource',
    'customQueryProperties',
  ],
})
  .linkTo(CrmObject, (b) =>
    b
      .findOther({
        fromSelfData: ({ data }) =>
          data.results.map(({ objectTypeId, objectId }, index) => ({
            params: { objectTypeId, objectId: objectId.toString() },
            context: index,
          })),
      })
      .push(({ self, other }) => {
        if (other.data) {
          const result = self.data.results[self.context];
          Object.assign(other.data.properties, result.properties);
          other.data.currentUserPermissions = result.currentUserPermissions;
        }
      })
      .pull(({ self, other }) => {
        if (!self.data) {
          return;
        }
        // refresh only the properties this search asked for — don't let the
        // full CrmObject widen the partial result's shape. A CrmSearch result's
        // key set is fixed by its `properties` request, so it stays
        // deterministic regardless of what else loaded the object.
        const result = self.data.results[self.context];
        if (!result) {
          return;
        }
        for (const name of self.params.properties) {
          const value = other.data.properties[name];
          if (value !== undefined) {
            result.properties[name] = value;
          }
        }
        result.currentUserPermissions = other.data.currentUserPermissions;
      })
  )
  .register();

export const CrmSearchAllProperties = defineResource({
  typeName: 'hs-resources:CrmSearchAllProperties',
  resolver: defaultCrmSearchAllPropertiesResolver,
  args: [
    'filterGroups',
    'objectTypeId',
    'sorts',
    'query',
    'count',
    'offset',
    'timeZoneId',
    'timeZoneSource',
    'customQueryProperties',
  ],
})
  .linkTo(CrmObject, (b) =>
    b
      .findOther({
        fromSelfData: ({ data }) =>
          data.results.map(({ objectTypeId, objectId }, index) => ({
            params: { objectTypeId, objectId: objectId.toString() },
            context: index,
          })),
      })
      .push(({ self }) => self.data.results[self.context])
      .pull(({ self, other }) => {
        if (self.data) {
          self.data.results[self.context] = other.data;
        }
      })
  )
  // Keep a partial CrmSearch in sync with an all-properties search of the same
  // objects. Both sides tag each result by objectTypeId:objectId, so `contexts`
  // gives every overlapping object as a { self: fullIndex, other: partialIndex }
  // pair — see the Pulse `contexts` primitive (only the first matched tag would
  // otherwise fire per change).
  .linkTo(CrmSearch, (b) =>
    b
      .tags({
        self: {
          fromData: ({ data }) =>
            data.results.map((result, index) => ({
              tag: `${result.objectTypeId}:${result.objectId}`,
              context: index,
            })),
        },
        other: {
          fromData: ({ data }) =>
            data.results.map((result, index) => ({
              tag: `${result.objectTypeId}:${result.objectId}`,
              context: index,
            })),
        },
      })
      .push(({ self, other, getContexts }) => {
        if (!other.data) {
          return;
        }
        // full -> partial: refresh only the properties the partial asked for,
        // keyed off its `properties` request — don't widen its result shape
        // with the full's extra properties.
        const requested = other.params.properties;
        for (const { self: fullIndex, other: partialIndex } of getContexts()) {
          const full = self.data.results[fullIndex];
          const partial = other.data.results[partialIndex];
          if (!full || !partial) {
            continue;
          }
          for (const name of requested) {
            const value = full.properties[name];
            if (value !== undefined) {
              partial.properties[name] = value;
            }
          }
        }
      })
      .pull(({ self, other, getContexts }) => {
        if (!self.data) {
          return;
        }
        // partial -> full: the partial only carries a subset, so merge its
        // values into the full object rather than replacing.
        for (const { self: fullIndex, other: partialIndex } of getContexts()) {
          const full = self.data.results[fullIndex];
          const partial = other.data.results[partialIndex];
          if (!full || !partial) {
            continue;
          }
          Object.assign(full.properties, partial.properties);
        }
      })
  )
  .register();
